import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useTheme } from '../../../../context/ThemeContext';
import { formatCurrency, formatDate, formatNumber } from '../../../../utils/format';

// Inflow / outflow pair, validated (dataviz validator) per mode: lightness
// band, chroma, colour-blind and normal-vision separation, contrast.
const COLORS = {
  light: { in: '#0891b2', out: '#b45309', line: '#334155', band: '#94a3b8', grid: '#e2e8f0', axis: '#64748b' },
  dark: { in: '#0e9fbb', out: '#d97706', line: '#e2e8f0', band: '#64748b', grid: '#334155', axis: '#94a3b8' },
};

export const useFlowMode = () => (useTheme().theme === 'dark' ? 'dark' : 'light');
export const useFlowColors = () => COLORS[useFlowMode()];

export const money = (v) => formatCurrency(v || 0, 'TRY');
const compact = (v) => formatNumber(v, { notation: 'compact', maximumFractionDigits: 1 });

/** "19 Eki" for a week, "Eki '26" for a month. */
export const periodLabel = (start, unit) => (unit === 'month'
  ? formatDate(start, { month: 'short', year: '2-digit' })
  : formatDate(start, { day: 'numeric', month: 'short' }));

const FlowTooltip = ({ active, payload, unit }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const line = (label, value, color) => (
    <div className="flex justify-between gap-6">
      <span className="text-surface-500 dark:text-surface-400">
        {color && <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: color }} />}
        {label}
      </span>
      <span className="font-medium tabular-nums text-surface-900 dark:text-surface-50">{money(value)}</span>
    </div>
  );
  return (
    <div className="rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 shadow-lg p-3 text-xs space-y-1 min-w-[220px]">
      <p className="font-semibold text-surface-900 dark:text-surface-50 mb-1">{periodLabel(row.start, unit)}</p>
      {line(t('accounting.cashFlow.columns.begin'), row.begin)}
      {line(t('accounting.cashFlow.chart.inflow'), row.inflow, row.colors.in)}
      {line(t('accounting.cashFlow.chart.outflow'), row.outflow, row.colors.out)}
      {line(t('accounting.cashFlow.columns.end'), row.end, row.colors.line)}
      <div className="border-t border-surface-100 dark:border-surface-800 pt-1 mt-1">
        {line(t('accounting.cashFlow.columns.low'), row.low)}
        {line(t('accounting.cashFlow.columns.high'), row.high)}
      </div>
    </div>
  );
};

/**
 * Cash-flow forecast: inflow bars up, outflow bars down (one TRY axis), the
 * projected end-of-period balance as a line, and the lowest / highest case
 * (every overdue / undated bill paid, every overdue / undated receivable
 * collected) as a band around it.
 */
const CashFlowChart = ({ data, height = 280, showLegend = true }) => {
  const { t } = useTranslation();
  const c = useFlowColors();
  if (!data?.periods?.length) return null;
  const rows = data.periods.map((p) => ({
    ...p, outflowDown: -p.outflow, band: [p.low, p.high], label: periodLabel(p.start, data.unit), colors: c,
  }));
  return (
    <div style={{ height }} data-testid="cash-flow-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} stackOffset="sign" barGap={2}>
          <CartesianGrid stroke={c.grid} strokeDasharray="0" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: c.axis, fontSize: 12 }} tickLine={false} axisLine={{ stroke: c.grid }} />
          <YAxis tickFormatter={compact} tick={{ fill: c.axis, fontSize: 12 }} width={56} tickLine={false} axisLine={false} />
          <Tooltip content={<FlowTooltip unit={data.unit} />} cursor={{ fill: c.grid, opacity: 0.4 }} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
          <ReferenceLine y={0} stroke={c.axis} strokeDasharray="3 3" />
          <Area dataKey="band" stroke="none" fill={c.band} fillOpacity={0.18} name={t('accounting.cashFlow.chart.range')}
            isAnimationActive={false} legendType="square" />
          <Bar dataKey="inflow" stackId="flow" fill={c.in} name={t('accounting.cashFlow.chart.inflow')}
            radius={[4, 4, 0, 0]} maxBarSize={36} />
          <Bar dataKey="outflowDown" stackId="flow" fill={c.out} name={t('accounting.cashFlow.chart.outflow')}
            radius={[0, 0, 4, 4]} maxBarSize={36} />
          <Line dataKey="end" stroke={c.line} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, fill: c.line }}
            activeDot={{ r: 5 }} name={t('accounting.cashFlow.chart.balance')} type="monotone" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CashFlowChart;
